import * as storage from "../utils/storage";
import { PDF_URL_KEY_NAME } from '../utils/constants';

//Pdf.js example https://mozilla.github.io/pdf.js/web/viewer.html
//Pdf.js constants define 
const DEFAULT_SCALE_DELTA = 1.1;
const MAX_SCALE = 3.5;
const MIN_SCALE = 1.0;
interface PDFViewerConfig {
  maxImageSize: number;
  textLayerMode: number;
  useOnlyCssZoom: boolean;
  defaultScaleValue: string;
  cMapUrl: string;
  cMapPacked: boolean;
}

interface OpenPdfParams {
  url: string;
}

class PDFViewerApplication {
  public static pdfViewer: any;

  private static pdfDocument: any;

  private pdfjsLib: any;
  private pdfjsViewer: any;
  private pdfViewerConfig: PDFViewerConfig;
  private pdfLoadingTask: any;
  private pdfLinkService: any;
  private pdfHistory: any;

  public constructor(lib: any, viewer: any, config: PDFViewerConfig) {
    this.pdfjsLib = lib;
    this.pdfjsViewer = viewer;
    this.pdfViewerConfig = config;
    this.pdfLoadingTask = null;
  }

  public async open(params: OpenPdfParams): Promise<any> {
    // Check pdf loading task
    if (this.pdfLoadingTask) {
      // We need to destroy already opened document
      return this.close().then(() => {
        // ... and repeat the open() call.
        return this.open(params);
      });
    }

    const config = this.pdfViewerConfig;
    const pdfUrl = params.url;

    const loadingTask = this.pdfjsLib.getDocument({
      url: pdfUrl,
      maxImageSize: config.maxImageSize,
      cMapUrl: config.cMapUrl,
      cMapPacked: config.cMapPacked,
    });
    this.pdfLoadingTask = loadingTask;

    loadingTask.onProgress = (progressData: {
      loaded: number;
      total: number;
    }): void => {
      const level = progressData.loaded / progressData.total;
      this.onProgress(level);
    };

    return loadingTask.promise.then(
      (pdfDocument: any) => {
        PDFViewerApplication.pdfDocument = pdfDocument;
        PDFViewerApplication.pdfViewer.setDocument(pdfDocument);
        this.pdfLinkService.setDocument(pdfDocument);
        this.pdfHistory.initialize({ fingerprint: pdfDocument.fingerprint });
        this.load(pdfDocument);
      },
      (error: any) => {
        console.log(error);
      }
    );
  }

  public async close(): Promise<any> {
    if (!this.pdfLoadingTask) {
      return Promise.resolve();
    }

    const promise = this.pdfLoadingTask.destroy();
    this.pdfLoadingTask = null;

    if (PDFViewerApplication.pdfDocument) {
      PDFViewerApplication.pdfDocument = null;
      PDFViewerApplication.pdfViewer.setDocument(null);
      this.pdfLinkService.setDocument(null, null);
    }

    return promise;
  }

  public load(pdfDocument: any): void {
    console.log(pdfDocument);
  }

  public initUI(container: string): void {
    const config = this.pdfViewerConfig;

    const linkService = new this.pdfjsViewer.PDFLinkService();
    this.pdfLinkService = linkService;

    const viewerContainer = document.getElementById(container);

    const pdfViewer = new this.pdfjsViewer.PDFViewer({
      container: viewerContainer,
      linkService: linkService,
      useOnlyCssZoom: config.useOnlyCssZoom,
      textLayerMode: config.textLayerMode,
    });

    PDFViewerApplication.pdfViewer = pdfViewer;
    linkService.setViewer(pdfViewer);

    this.pdfHistory = new this.pdfjsViewer.PDFHistory({
      linkService: linkService,
    });
    linkService.setHistory(this.pdfHistory);
  }

  public resize(): void {
    const { pdfDocument, pdfViewer } = PDFViewerApplication;
    if (!pdfDocument) {
      return;
    }
    const currentScaleValue = pdfViewer.currentScaleValue;
    if (
      currentScaleValue === 'auto' ||
      currentScaleValue === 'page-fit' ||
      currentScaleValue === 'page-width'
    ) {
      // Note: the scale is constant for 'page-actual'.
      pdfViewer.currentScaleValue = currentScaleValue;
    }
    pdfViewer.update();
  }

  public zoomReset():void {
    if (PDFViewerApplication.pdfViewer.isInPresentationMode) {
      return;
    }
    PDFViewerApplication.pdfViewer.currentScaleValue = this.pdfViewerConfig.defaultScaleValue;
  }

  public zoomIn(ticks:any = 0): void {  
    var newScale = PDFViewerApplication.pdfViewer.currentScale;
    do {
      newScale = (newScale * DEFAULT_SCALE_DELTA).toFixed(2);
      newScale = Math.ceil(newScale * 10) / 10; 
      newScale = Math.min(MAX_SCALE, newScale); 
    } while (--ticks > 0 && newScale < MAX_SCALE);
    PDFViewerApplication.pdfViewer.currentScaleValue = newScale;
  }
  public zoomOut(ticks:any =0 ): void {
    var newScale = PDFViewerApplication.pdfViewer.currentScale;
    do {
      newScale = (newScale / DEFAULT_SCALE_DELTA).toFixed(2);
      newScale = Math.floor(newScale * 10) / 10;
      newScale = Math.max(MIN_SCALE, newScale);
    } while (--ticks > 0 && newScale > MIN_SCALE);
    PDFViewerApplication.pdfViewer.currentScaleValue = newScale;
  }

  public prevPage(): void {
    PDFViewerApplication.currentPage--;
  }

  public nextPage(): void {
    PDFViewerApplication.currentPage++;
  }

  public goToPage(value: number): void {
    PDFViewerApplication.currentPage = value || 0;
    // Ensure that the page number input displays the correct value,
    // even if the value entered by the user was invalid
    // (e.g. a floating point number).
    if (value !== PDFViewerApplication.currentPage) {
      value = PDFViewerApplication.currentPage;
    }
  }

  public onProgress(level: number): void {
    const percent = Math.round(level * 100);
    console.log(percent);
  }

  public static animationStartedPromise = new Promise(resolve => {
    window.requestAnimationFrame(resolve);
  });

  public static get pagesCount(): number {
    return this.pdfDocument ? this.pdfDocument.numPages : 0;
  }

  public static get currentPage(): number {
    return this.pdfViewer.currentPageNumber;
  }
  public static set currentPage(page: number) {
    this.pdfViewer.currentPageNumber = page;
  }
}

(() => {
  const pdfUrl = storage.session.getItem(PDF_URL_KEY_NAME);
  // 没有 PDF ID 时报错并返回 404 页面
  if (!pdfUrl || pdfUrl === 'undefined') { // sessionStorage.getItem 有时会返回字符串格式的 undefined
    location.href = '/404';
    throw ReferenceError('Error: can not find pdf files.');
  }
  const pdfPath = `${pdfUrl}`;
  // const pdfViewer = <HTMLEmbedElement>document.getElementById('pdf-viewer');
  // if (!pdfViewer) {
  //   throw ReferenceError('PDF Viewer not found.');
  // } else {
  //   pdfViewer.src = pdfPath;
  // }

  /** PDF.js */
  // @ts-ignore
  const pdfjsLib: any = window.pdfjsLib;
  // @ts-ignore
  const pdfjsWorker: any = window.pdfjsWorker;
  // @ts-ignore
  const pdfjsViewer: any = window.pdfjsViewer;

  if (!(!!pdfjsLib || !!pdfjsWorker || !!pdfjsViewer)) {
    const errorInfo = `Error: PDF 查看器初始化失败，请检查您的网络并尝试重新加载，
      或您的浏览器过旧无法初始化。`;
    const pdfStatus = `PDFLib: ${!!pdfjsLib}, PDFWorker: ${!!pdfjsWorker}, 
      PDFViewer: ${!!pdfjsViewer}.`;
    const info = `${errorInfo}${pdfStatus}`;
    document.body.innerHTML = info;
    throw ReferenceError(info);
  }

  const config: PDFViewerConfig = {
    maxImageSize: 2048 * 2048,
    textLayerMode: 0,
    useOnlyCssZoom: true,
    defaultScaleValue: 'auto',
    cMapUrl: `${location.origin}/themes/oc-chbaf-theme/assets/venders/pdfjs/cmaps/`,
    cMapPacked: true,
  };

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      const pdfViewer = new PDFViewerApplication(pdfjsLib, pdfjsViewer, config);
      const countElement = <HTMLInputElement>document.getElementById('pageCount');
      const inputElement = <HTMLInputElement>document.getElementById('pageNumber');
      const prevButton = <HTMLButtonElement>document.getElementById('previous');
      const nextButton = <HTMLButtonElement>document.getElementById('next');
      const narrowButton = <HTMLButtonElement>document.getElementById('narrow');
      const enlargeButton = <HTMLButtonElement>document.getElementById('enlarge');
      const loadingLabel = <HTMLLabelElement>document.getElementById('loadingLabel');
      const loadingCount = <HTMLInputElement>document.getElementById('loadingCount');

      const pdfLoad = (pdfDocument: any): void => {
        const pageCount = pdfDocument.numPages || 0;
        countElement.value = pageCount;
        inputElement.max = pageCount;
      };

      const pdfOnProgress = (level: number): void => {
        const percent = Math.round(level * 100);
        if (percent < 100) {
          loadingCount.value = `${percent}%`;
        } else {
          loadingLabel.style.display = 'none';
          loadingCount.style.display = 'none';
        }
      };

      pdfViewer.load = pdfLoad;
      pdfViewer.onProgress = pdfOnProgress;
      pdfViewer.initUI('viewerContainer');

      PDFViewerApplication.animationStartedPromise.then(() => {
        pdfViewer.open({
          url: pdfPath,
        });
      });

      window.addEventListener('resize', () => {
        pdfViewer.resize();
      });

      document.addEventListener('pagesinit', () => {
        // We can use pdfViewer now, e.g. let's change default scale.
        PDFViewerApplication.pdfViewer.currentScaleValue = config.defaultScaleValue;
        if (PDFViewerApplication.currentPage <= 1) {
          prevButton.disabled = true;
        }
      });

      document.addEventListener(
        'pagechanging',
        (evt: Event) => {
          // @ts-ignore
          const page = evt.detail.pageNumber;
          const numPages = PDFViewerApplication.pagesCount;
          inputElement.value = page;
          prevButton.disabled = page <= 1;
          nextButton.disabled = page >= numPages;
        },
        true
      );

      prevButton.addEventListener('click', function () {
        pdfViewer.prevPage();
      });
      nextButton.addEventListener('click', function () {
        pdfViewer.nextPage();
      });
      enlargeButton.addEventListener('click',function(){
        pdfViewer.zoomIn();
      });
      narrowButton.addEventListener('click', function () {
        pdfViewer.zoomOut();
      });
      inputElement.addEventListener('click', () => {
        inputElement.select();
      });
      inputElement.addEventListener('change', () => {
        const page = Number(inputElement.value) || 0;
        pdfViewer.goToPage(page);
      });
    },
    true
  );
})();
